import React from "react"
import {
  Box,
  Chip,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material"
import { NAV_CAT_MAP } from "@/config/defaults"
import {
  CARGO_OPTIONS,
  LOAD_STATE_OPTIONS,
  validateDecimal,
} from "@/features/voyagePlanner/voyageSettingsFields"
import { SECTION_LABEL_SX } from "@/features/voyagePlanner/wizard/styles"
import { FIELD_LABEL_SX } from "@/theme/panelStyles"
import DecimalField from "@/components/ui/DecimalField"
import { vesselWeatherLimits } from "@/features/vessel/vesselMeta.js"

const SELECT_SX = {
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#004f7d",
  "& .MuiSelect-select": { py: 0.6, px: 1 },
}

// Empty = inherit the vessel default, shown as placeholder.
const INHERITABLE = { minValue: Number.EPSILON, allowEmpty: true }

// Editable voyage/routing settings — edits the draft copy; committed into
// plannerVesselSettings when leaving the Options step (validation-gated).
function VoyageSpecTab({
  draftPlannerVesselSettings,
  setDraftPlannerVesselSettings,
  optionsErrors,
  setOptionsError,
  bunkerPrices,
  setBunkerPrices,
  routingInactive = false,
  vesselMeta = null,
}) {
  const s = draftPlannerVesselSettings ?? {}
  const inherited = vesselWeatherLimits(vesselMeta)

  const handleBunkerPrice = (field) => (event) => {
    const { value } = validateDecimal(event.target.value)
    setBunkerPrices((prev) => ({ ...prev, [field]: value }))
  }

  const handleDecimal =
    (field, opts = {}) =>
    (event) => {
      const { value, invalid } = validateDecimal(event.target.value, opts)
      setOptionsError(field, invalid)
      setDraftPlannerVesselSettings((prev) => ({ ...prev, [field]: value }))
    }

  const handleSelect = (field) => (event) => {
    const value = event.target.value
    setDraftPlannerVesselSettings((prev) => ({ ...prev, [field]: value }))
  }

  const handleCargoToggle = (key) => () => {
    setDraftPlannerVesselSettings((prev) => ({
      ...prev,
      cargo: { ...prev.cargo, [key]: !prev.cargo?.[key] },
    }))
  }

  const cargoCount = CARGO_OPTIONS.filter(({ key }) => s.cargo?.[key]).length

  return (
    <>
      <Typography sx={{ fontSize: "10.5px", color: "#8a98a6", my: 1.25 }}>
        Voyage & routing settings for weather-based pathfinding and RNW
        restriction checks
      </Typography>

      <Typography sx={SECTION_LABEL_SX}>Weather Limits</Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 0.75,
          mb: 1.5,
        }}
      >
        <DecimalField
          label="Wind [kn]"
          tooltip="Wind speed threshold beyond which the vessel's performance or safety is significantly affected."
          value={s.windLimit}
          inheritedValue={inherited.windLimit}
          error={Boolean(optionsErrors.windLimit)}
          onChange={handleDecimal("windLimit", INHERITABLE)}
          helperText="Enter a positive number"
        />
        <DecimalField
          label="Wave [m]"
          tooltip="Wave height threshold beyond which the vessel's speed is significantly reduced."
          value={s.waveLimit}
          inheritedValue={inherited.waveLimit}
          error={Boolean(optionsErrors.waveLimit)}
          onChange={handleDecimal("waveLimit", INHERITABLE)}
          helperText="Enter a positive number"
        />
        <DecimalField
          label="Current [kn]"
          tooltip="Ocean current speed threshold beyond which the vessel's performance or safety is significantly affected."
          value={s.currentLimit}
          inheritedValue={inherited.currentLimit}
          error={Boolean(optionsErrors.currentLimit)}
          onChange={handleDecimal("currentLimit", INHERITABLE)}
          helperText="Enter a positive number"
        />
      </Box>

      {setBunkerPrices && (
        <>
          <Typography sx={SECTION_LABEL_SX}>Bunker Prices</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 0.75,
              mb: 0.5,
            }}
          >
            <DecimalField
              label="Bunker price [USD/t]"
              tooltip="Bunker price per ton outside Emission Control Areas."
              value={bunkerPrices?.usdPerTon}
              onChange={handleBunkerPrice("usdPerTon")}
              error={!(Number(bunkerPrices?.usdPerTon) > 0)}
            />
            <DecimalField
              label="ECA bunker price [USD/t]"
              tooltip="Price per ton inside ECAs (low-sulphur fuel)."
              value={bunkerPrices?.ecaUsdPerTon}
              onChange={handleBunkerPrice("ecaUsdPerTon")}
              error={!(Number(bunkerPrices?.ecaUsdPerTon) > 0)}
            />
          </Box>
          <Typography sx={{ fontSize: "9.5px", color: "#8a98a6", mb: 1.5 }}>
            Required for fuel cost reporting and the Fuel optimisation mode.
          </Typography>
        </>
      )}

      <Box
        inert={routingInactive || undefined}
        sx={routingInactive ? { opacity: 0.45, pointerEvents: "none" } : null}
      >
        <Typography sx={SECTION_LABEL_SX}>RNW Restriction Parameters</Typography>
        {routingInactive && (
          <Typography sx={{ fontSize: "9.5px", color: "#8a98a6", mb: 0.75 }}>
            Not used in Manual mode: the route is fixed, nothing is routed.
          </Typography>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0.75,
            mb: 1.5,
          }}
        >
          <DecimalField
            label="Safety Depth [m]"
            tooltip="Route is considered safe if available depth is not less than this required safety depth."
            value={s.safetyDepthM}
            error={Boolean(optionsErrors.safetyDepthM)}
            onChange={handleDecimal("safetyDepthM", { minValue: Number.EPSILON })}
            helperText="Enter a positive number"
          />
          <DecimalField
            label="Air Draft [m]"
            tooltip="Vessel height (air draft) used to assess vertical clearances such as bridges or overhead structures."
            value={s.airDraftM}
            error={Boolean(optionsErrors.airDraftM)}
            onChange={handleDecimal("airDraftM", { minValue: Number.EPSILON })}
            helperText="Enter a positive number"
          />
          <DecimalField
            label="Simplification Dist. [m]"
            tooltip="Minimum distance used to simplify route geometry and reduce computational complexity."
            value={s.simplifyEpsilonM}
            error={Boolean(optionsErrors.simplifyEpsilonM)}
            onChange={handleDecimal("simplifyEpsilonM")}
            helperText="Enter a non-negative number"
          />
          <Box>
            <Tooltip
              placement="top"
              title="Defines the general safety level or operational limits for voyage planning."
            >
              <Typography sx={FIELD_LABEL_SX}>Navigability Category</Typography>
            </Tooltip>
            <Select
              fullWidth
              size="small"
              value={s.navigabilityCategory || ""}
              onChange={handleSelect("navigabilityCategory")}
              sx={SELECT_SX}
            >
              {Object.keys(NAV_CAT_MAP).map((key) => (
                <MenuItem key={key} value={key} sx={{ fontSize: "13px" }}>
                  {key}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Tooltip
              placement="top"
              title="Indicates current vessel loading condition (e.g., Ballast, Laden)."
            >
              <Typography sx={FIELD_LABEL_SX}>Load State</Typography>
            </Tooltip>
            <Select
              fullWidth
              size="small"
              value={s.loadstate || ""}
              onChange={handleSelect("loadstate")}
              sx={SELECT_SX}
            >
              {LOAD_STATE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt} sx={{ fontSize: "13px" }}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mb: 0.75 }}>
          <Typography sx={{ ...SECTION_LABEL_SX, mb: 0 }}>Cargo</Typography>
          <Typography sx={{ ml: "auto", fontSize: "10px", color: "#8a98a6" }}>
            {cargoCount === 0 ? "none selected" : `${cargoCount} selected`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
          {CARGO_OPTIONS.map(({ key, label, tooltip }) => {
            const active = Boolean(s.cargo?.[key])
            return (
              <Tooltip key={key} title={tooltip}>
                <Chip
                  label={label}
                  size="small"
                  onClick={handleCargoToggle(key)}
                  sx={{
                    borderRadius: "14px",
                    fontSize: "11px",
                    fontWeight: 600,
                    border: active ? "1px solid #004f7d" : "1px solid #c4cdd5",
                    bgcolor: active ? "#004f7d" : "#fff",
                    color: active ? "#fff" : "#004f7d",
                    "&:hover": {
                      bgcolor: active ? "#01426a" : "rgba(0,79,125,0.06)",
                    },
                  }}
                />
              </Tooltip>
            )
          })}
        </Box>
      </Box>
    </>
  )
}

export default React.memo(VoyageSpecTab)
